import { useFacilitiesList } from "@features/ax/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { LocationMap } from "@shared/components/LocationMap";
import { PageHeader } from "@shared/components/PageHeader";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { toast } from "@shared/ui/use-toast";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCreateSystems,
  useGetAllUserInfo,
  useGetChoices,
  useGetSystemDataById,
  useGetSystemType,
  useUpdateSystems,
} from "../../hooks";

interface StepHelpers {
  getStepData: (stepIndex: number) => any;
  setStepData: (stepIndex: number, data: any) => void;
}

interface BasicsLocationStepProps {
  editMode: boolean;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  globalFormData?: any;
  updateGlobalFormData?: (data: any) => void;
}

export const BasicsLocationStep: React.FC<BasicsLocationStepProps> = ({
  editMode,
  stepHelpers,
  currentStepIndex = 0,
  data = {},
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  setValidationError,
  globalFormData,
  updateGlobalFormData,
  clearValidationError,
  isValidating,
}) => {
  const { systemsId, action } = useParams();
  const navigate = useNavigate();
  const dataLoadedRef = useRef(false);
  const [facilityId, setFacilityId] = useState([]);

  // API hooks
  const { data: systemData } = useGetSystemDataById(Number(systemsId));
  const { data: facilitiesData = [] } = useFacilitiesList();
  const { data: choicesData = [] } = useGetSystemType("system_type", true);
  const { data: statusData = [] } = useGetChoices("system_status");
  const { data: backupStatusData = [] } = useGetChoices("backup_status");
  const { data: operationalHoursData = [] } =
    useGetChoices("operational_hours");
  const { data: contactPersonData = [] } = useGetAllUserInfo();
  const [isAddressPopulated, setIsAddressPopulated] = useState(false);

  // API mutation hooks
  const { mutateAsync: createSystem, isPending: isCreating } =
    useCreateSystems();
  const { mutateAsync: updateSystem, isPending: isUpdating } = useUpdateSystems(
    Number(systemsId)
  );

  // Local state - Initialize with data from props
  const [locationData, setLocationData] = useState({
    latitude: 0,
    longitude: 0,
    address: "",
  });

  // Memoized options with safe defaults
  const facilityOptions = useMemo(
    () =>
      facilitiesData.map((facility) => ({
        value: facility.id,
        label: facility.facilityName,
      })),
    [facilitiesData]
  );

  const systemTypeOptions = useMemo(
    () =>
      choicesData.map((choice) => ({
        value: choice.code,
        label: choice.name,
      })),
    [choicesData]
  );

  const statusOptions = useMemo(
    () =>
      statusData.map((choice) => ({
        value: choice.key,
        label: choice.value,
      })),
    [statusData]
  );

  const backupStatusOptions = useMemo(
    () =>
      backupStatusData.map((choice) => ({
        value: choice.key,
        label: choice.value,
      })),
    [backupStatusData]
  );

  const operationalHoursOptions = useMemo(
    () =>
      operationalHoursData.map((choice) => ({
        value: choice.key,
        label: choice.value,
      })),
    [operationalHoursData]
  );

  const contactPersonOptions = useMemo(
    () =>
      contactPersonData.map((choice) => ({
        value: choice.user.id,
        label: choice.user.name,
        email: choice.user.email,
        phone: choice.user.phone || "",
      })),
    [contactPersonData]
  );

  // Location handlers
  const handleLatitudeChange = (value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < -90 || numValue > 90) {
      toast({
        title: "Invalid Latitude",
        description: "Latitude must be between -90 and 90 degrees.",
        variant: "destructive",
      });
      return;
    }
    const updatedLocationData = { ...locationData, latitude: numValue };
    setLocationData(updatedLocationData);
    // Also update the main data object
    const updatedData = { ...data, latitude: numValue };
    onDataChange?.(updatedData);
  };

  const handleLongitudeChange = (value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < -180 || numValue > 180) {
      toast({
        title: "Invalid Longitude",
        description: "Longitude must be between -180 and 180 degrees.",
        variant: "destructive",
      });
      return;
    }
    const updatedLocationData = { ...locationData, longitude: numValue };
    setLocationData(updatedLocationData);
    // Also update the main data object
    const updatedData = { ...data, longitude: numValue };
    onDataChange?.(updatedData);
  };

  const handleLocationChange = (lat: number, lng: number) => {
    const updatedLocationData = {
      ...locationData,
      latitude: lat,
      longitude: lng,
    };
    setLocationData(updatedLocationData);
    // Also update the main data object
    const updatedData = { ...data, latitude: lat, longitude: lng };
    onDataChange?.(updatedData);
  };

  const handleAddressChange = (address) => {
    const updatedLocationData = { ...locationData, address };
    setLocationData(updatedLocationData);
    // Also update the main data object
    const updatedData = { ...data, address };
    onDataChange?.(updatedData);
  };

  const handleFacilityChange = (value: []) => {
    setFacilityId(value);
  };

  // Form fields
  const formFields: FormField[] = useMemo(
    () => [
      // Basic Information Section
      {
        name: "systemName",
        type: "text",
        label: "System Name",
        group: "Basic Information",
        placeholder: "Enter system name",
        showHelperTooltip: true,
        tooltipText:
          "System Name Provide a descriptive name that clearly identifies the system's location and function, making it easily recognizable to staff and stakeholders. A good name should include relevant location details and system purpose for easy identification across your organization.",
        required: true,
        defaultValue: data.systemName || "",
      },
      {
        name: "systemType",
        type: "select",
        label: "System Type",
        group: "Basic Information",
        placeholder: "Select system type...",
        showHelperTooltip: true,
        tooltipText:
          "System Type Choose the category that best describes your system's primary function or classification within your organization's infrastructure. This classification helps determine applicable regulations, monitoring requirements, and operational procedures specific to your system type. If the appropriate system type is not available in the dropdown, add it in the System Types configuration section before proceeding.",
        required: true,
        options: systemTypeOptions,
        defaultValue: data?.systemType || "",
      },
      {
        name: "facilityId",
        type: "select",
        label: "Facility",
        group: "Basic Information",
        groupColorNeeded: false,
        placeholder: "Select facility...",
        showHelperTooltip: true,
        tooltipText:
          "Facility Select the specific facility where this system is physically located, as each system must be associated with a particular facility for proper organization and management. If your facility doesn't appear in the dropdown list, add the facility in the Facility tab before proceeding with system registration.",
        required: true,
        options: facilityOptions,
        defaultValue: data?.facilityId || "",
      },
      {
        name: "status",
        type: "select",
        label: "Status",
        group: "Basic Information",
        placeholder: "Select status",
        required: false,
        showHelperTooltip: true,
        tooltipText:
          "Status Indicate the current operational phase of your system, whether it's in the planning stage, under construction, fully operational, temporarily out of service for maintenance, or permanently decommissioned. This status affects reporting requirements and helps track the system's lifecycle.",
        options: statusOptions,
        defaultValue: data?.status || "",
      },
      {
        name: "commissionDate",
        type: "datepicker",
        label: "Commissioned Date",
        group: "Basic Information",
        placeholder: "Select date",
        required: true,
        showHelperTooltip: true,
        allowFuture: false,
        tooltipText:
          "Commissioned Date Enter the date when the system became operational if it's currently in service, or the expected commissioning date if it's still in planning or construction phases. This date is important for compliance tracking, asset management, and maintenance scheduling.",
        defaultValue: data?.commissionDate || "",
      },
      {
        name: "backupStatus",
        type: "select",
        label: "Backup Status",
        group: "Basic Information",
        placeholder: "Select backup status",
        required: false,
        showHelperTooltip: true,
        tooltipText:
          "Backup Status Specify whether backup systems or alternative operational methods are available in case of primary system failure, choosing from no backup, partial backup capabilities, or full redundancy. This information is essential for risk assessment and business continuity planning.",
        options: backupStatusOptions,
        defaultValue: data?.backupStatus || "",
      },
      {
        name: "operationalHours",
        type: "select",
        label: "Operational Hours",
        group: "Basic Information",
        placeholder: "Select operational hours",
        required: false,
        showHelperTooltip: true,
        tooltipText:
          "Operational Hours Define the normal operating schedule for this system, which may be continuous operation, specific daily hours, or intermittent operation based on demand. This schedule affects maintenance planning, resource allocation, and operational costs. If the required operational hours are not available in the dropdown, add them in the Operational Hours configuration section.",
        options: operationalHoursOptions,
        defaultValue: data?.operationalHours || "",
      },

      // Contact Information Section
      {
        name: "contactPerson",
        type: "select",
        label: "Contact Person",
        group: "Contact Information",
        placeholder: "Select contact person...",
        showHelperTooltip: true,
        tooltipText:
          "Contact Person Enter the name of the primary individual responsible for this system's operation and maintenance, who should be knowledgeable about daily operations and authorized to make operational decisions. This person will receive system notifications, alerts, and routine communications, so choose someone who is actively involved with the system.",
        required: false,
        groupColorNeeded: false,
        options: contactPersonOptions,
        defaultValue: data?.contactPerson || "",
      },
      {
        name: "contactEmail",
        type: "email",
        label: "Contact",
        group: "Contact Information",
        placeholder: "Enter contact email",
        showHelperTooltip: true,
        tooltipText:
          "Email Provide a current and regularly monitored email address for the primary contact person, as this will be used for automated system alerts, notifications, maintenance reminders, and important communications. Ensure this email address is kept up to date and that the recipient checks it frequently for time-sensitive information.",
        required: false,
        disabled: true,
        defaultValue: data?.contactEmail || "",
      },
      // Location Information Section
      {
        name: "location_map",
        type: "text",
        label: "Map Pin Location",
        group: "Location Information",
        groupColorNeeded: false,
        required: false,
        showHelperTooltip: true,
        tooltipText:
          "Map Pin Location Click directly on the interactive map to set the precise GPS coordinates of your system, which creates a visual reference for mapping, emergency response, and asset management. You can drag the pin to fine-tune the location if the initial placement isn't perfectly accurate.",
        className: "col-span-2 mt-3",
        customRender: () => (
          <div className="space-y-4">
            {/* Coordinate Input Fields */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Latitude
                </label>
                <Input
                  type="number"
                  step="any"
                  placeholder="e.g., 40.7128"
                  value={locationData.latitude || ""}
                  onChange={(e) => handleLatitudeChange(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Longitude
                </label>
                <Input
                  type="number"
                  step="any"
                  placeholder="e.g., -74.0060"
                  value={locationData.longitude || ""}
                  onChange={(e) => handleLongitudeChange(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Address / Site Label
                </label>
                <Input
                  type="text"
                  placeholder="Address will appear here"
                  value={locationData.address || ""}
                  className="w-full bg-gray-50 "
                  onChange={(e) => handleAddressChange(e.target.value)}
                />
              </div>
            </div>

            {/* Map Component */}
            <LocationMap
              latitude={locationData.latitude}
              longitude={locationData.longitude}
              onLocationChange={handleLocationChange}
              onAddressChange={handleAddressChange}
            />
          </div>
        ),
      },
      {
        name: "description",
        type: "textarea",
        label: "Description / Purpose",
        group: "Location Information",
        placeholder: "Describe the system's purpose and key characteristics...",
        required: false,
        showHelperTooltip: true,
        tooltipText:
          "Description/Purpose Write a comprehensive description of the system's function, capacity, key components, and any special operational characteristics that would help others understand its role in your infrastructure. Include details about processes, capacity ratings, unique features, and any operational constraints or requirements.",
        rows: 4,
        defaultValue: data?.description || "",
      },
    ],
    [
      data,
      facilityOptions,
      systemTypeOptions,
      statusOptions,
      backupStatusOptions,
      operationalHoursOptions,
      contactPersonOptions,
      locationData,
    ]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: data,
  });

  // Data initialization effect
  useEffect(() => {
    if (dataLoadedRef.current) return;

    switch (action) {
      case "edit":
        if (systemData) {
          const editData = {
            facilityId: systemData.facility,
            systemType: systemData.systemType,
            systemName: systemData.systemName,
            status: Number(systemData.status),
            commissionDate: systemData.commissionedDate,
            backupStatus: Number(systemData.backupStatus),
            operationalHours: Number(systemData.operationalHours),
            address: systemData.location,
            latitude: systemData.latitude || "",
            longitude: systemData.longitude || "",
            location: systemData.location,
            description: systemData.description,
            contactPerson: systemData.remoteContactUserId,
            contactEmail: systemData.contactEmail,
            contactPhone: systemData.contactPhone,
          };

          setLocationData({
            latitude: systemData.latitude || "",
            longitude: systemData.longitude || "",
            address: systemData.location || "",
          });

          onDataChange?.(editData);
          FormService.populateForm(form, editData, formFields);
          dataLoadedRef.current = true;
        }
        break;

      default:
        // For create mode
        if (stepHelpers) {
          const savedData = stepHelpers.getStepData(currentStepIndex) || {};
          if (Object.keys(savedData).length > 0) {
            setLocationData({
              latitude: savedData.latitude || "",
              longitude: savedData.longitude || "",
              address: savedData.address || "",
            });
            onDataChange?.(savedData);
          }
        }
        dataLoadedRef.current = true;
        break;
    }
  }, [
    action,
    systemData,
    stepHelpers,
    currentStepIndex,
    onDataChange,
    form,
    formFields,
  ]);

  // Enhanced sync effect to properly handle data changes
  useEffect(() => {
    if (
      data.latitude !== undefined &&
      data.longitude !== undefined &&
      data.address !== undefined
    ) {
      setLocationData({
        latitude: data.latitude || "",
        longitude: data.longitude || "",
        address: data.address || "",
      });
    }
  }, [data.latitude, data.longitude, data.address]);

  // Additional effect to handle initial data from props
  useEffect(() => {
    if (
      !dataLoadedRef.current &&
      (data.latitude || data.longitude || data.address)
    ) {
      setLocationData({
        latitude: data.latitude || "",
        longitude: data.longitude || "",
        address: data.address || "",
      });
    }
  }, [data]);

  const handleFieldChange = (fieldName, value) => {
    switch (fieldName) {
      case "contactPerson":
        if (!value) {
          // Use timeout to avoid stack overflow
          setTimeout(() => {
            form.setValue("contactEmail", "");
            form.setValue("contactPhone", "");
          }, 0);
          return;
        }

        const selected = contactPersonOptions.find(
          (opt) => opt.value === Number(value)
        );

        if (selected) {
          // Use timeout to avoid stack overflow
          setTimeout(() => {
            form.setValue("contactEmail", selected.email || "");
            form.setValue("contactPhone", selected.phone || "");
          }, 0);
        }

        // Update data without setValue to avoid loops
        onDataChange?.({ ...data, [fieldName]: value });
        return;

      case "address":
        const updatedLocationData = { ...locationData, address: value };
        setLocationData(updatedLocationData);
        break;

      case "facilityId":
        handleFacilityChange(value);
        if (action !== "edit") {
          if (isAddressPopulated) {
            return;
          }
          const selectedFacility = facilitiesData.find(
            (opt) => opt.id === Number(value)
          );

          setLocationData({
            latitude: selectedFacility?.latitude || "",
            longitude: selectedFacility?.longitude || "",
            address: selectedFacility?.addressSiteLabel || "",
          });
          setTimeout(() => {
            setLocationData((prev) => ({
              ...prev,
              address: selectedFacility?.addressSiteLabel || "",
            }));
          }, 1000);
        }
        break;

      default:
        break;
    }

    onDataChange?.({ ...data, [fieldName]: value });
  };

  const buildPayload = (formData) => {
    const selectedContact = contactPersonOptions.find(
      (opt) => opt.value === Number(formData.contactPerson)
    );

    const basePayload = {
      systemName: formData.systemName,
      facility: Number(formData.facilityId),
      systemType: formData.systemType,
      status: formData.status,
      commissionedDate: formData.commissionDate,
      backupStatus: Number(formData.backupStatus),
      operationalHours: Number(formData.operationalHours),
      description: formData.description,
      remoteUtilityId: getRemoteUtilityId(),
      location: locationData.address || formData.address,
      remoteContactUserId: selectedContact?.value || null,
      contactPersonName: selectedContact?.label || "",
      contactEmail: formData.contactEmail,
      contactPhone: formData.contactPhone,
    };

    if (locationData.latitude && locationData.longitude) {
      basePayload["latitude"] = locationData.latitude;
      basePayload["longitude"] = locationData.longitude;
    }

    const cleanedPayload = Object.fromEntries(
      Object.entries(basePayload).filter(
        ([, value]) => value !== "" && value !== null && value !== undefined
      )
    );

    return cleanedPayload;
  };

  const handleNext = async () => {
    const isValid = await FormService.validateForm(form);

    if (!isValid) {
      if (setValidationError) {
        setValidationError("Please fill in all required fields.");
      }
      toast({
        title: "Please fill in all required fields.",
        variant: "destructive",
      });

      return;
    }
    const formData = form.getValues();

    // Merge form data with current location data
    const mergedData = {
      ...data,
      ...formData,
      latitude: locationData.latitude,
      longitude: locationData.longitude,
      address: locationData.address,
    };

    try {
      const payload = buildPayload(mergedData);
      let response;
      let systemDraftId;

      switch (action) {
        case "edit":
          response = await updateSystem(payload);
          systemDraftId = stepHelpers?.getStepData(0)?.id || systemsId;
          break;

        case "create":
          response = await createSystem(payload);
          systemDraftId = response?.id;
          break;
      }

      if (systemDraftId) {
        const completedData = {
          ...mergedData,
          payload,
          systemDraftId,
          response,
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        };

        onDataChange?.(completedData);
        onNext?.();
        navigate(`/ax/asset-registry/systems/edit/${systemDraftId}#operations`);
      } else {
        setValidationError?.(
          "Failed to create system draft. Please try again."
        );
      }
    } catch (error) {
      setValidationError?.("Failed to submit. Please try again.");
    }
  };

  return (
    <div className="bg-white p-3">
      <div className="mb-2">
        <PageHeader
          title="Basic & Location Information"
          subtitle="Specify the system's basic information, contact information, and location details."
        />
      </div>

      <DynamicForm
        fields={formFields}
        form={form}
        onFieldChange={handleFieldChange}
        gridColumns={2}
        responsiveColumns={{ sm: 1, md: 2 }}
        config={{
          layout: "grid",
          columns: 2,
          showSubmitButton: false,
          showResetButton: false,
          spacing: "medium",
          classes: FIELD_CLASSES,
        }}
      />

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-6 pt-6 border-t border-gray-200">
        <Button onClick={onPrevious} disabled={isFirstStep} variant="outline">
          Previous
        </Button>
        <Button
          onClick={handleNext}
          disabled={isValidating || isCreating || isUpdating}
        >
          {isCreating || isUpdating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
              {isCreating ? "Creating..." : "Updating..."}
            </>
          ) : isValidating ? (
            "Validating..."
          ) : (
            "Continue to Next Step"
          )}
        </Button>
      </div>
    </div>
  );
};
